import { Link } from "react-router-dom";
import { ArrowRight, Handshake, HardHat, Leaf, ShieldCheck, Truck } from "lucide-react";
import { Eyebrow, Page, PageHeader, Reveal } from "@/components/Reveal";
import { IMAGES, PHONE_DISPLAY, PHONE_TEL } from "@/constants/site";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Safety First",
    copy: "Crews briefed on site hazards and safe work practices before every shift, in line with WorkSafeBC expectations.",
  },
  {
    icon: Handshake,
    title: "Fair & Honest Dealing",
    copy: "Straightforward rates, fair treatment of workers, and no surprises on your invoice. Trust is the whole business.",
  },
  {
    icon: Leaf,
    title: "Respect For The Crop",
    copy: "Careful hands reduce damage and waste. We train crews to handle produce the way growers expect.",
  },
  {
    icon: HardHat,
    title: "Show Up Ready",
    copy: "Punctual, properly dressed and prepared for the work — whether that's mud, greenhouse heat or a cold storage line.",
  },
];

const STEPS = [
  { num: "01", title: "Tell us the job", copy: "Call or send the form — crop, location, crew size, dates and hours." },
  { num: "02", title: "We assemble the crew", copy: "We match experienced workers to your operation and confirm the details." },
  { num: "03", title: "The crew arrives ready", copy: "On time, supervised where needed, and ready to work from hour one." },
];

export default function About() {
  return (
    <Page>
      <PageHeader
        eyebrow="About Us"
        title={
          <>
            Rooted in BC agriculture. <span className="italic text-[#86EFAC]">Built on hard work.</span>
          </>
        }
        copy="Barnala Labour Contracting connects farms, greenhouses and canneries across the Lower Mainland, Fraser Valley and Greater Vancouver with dependable, experienced labour crews."
      />

      <section className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img src={IMAGES.farmField} alt="Green fields on a BC farm" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow>Our Story</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                A contractor who understands the season.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-[#F6F4EE]/70">
                <p>
                  Barnala Labour Contracting was built on a simple promise: when a grower or processor calls, the crew
                  shows up. Farming doesn't wait — berries ripen, greenhouse crops turn, and cannery lines run on tight
                  schedules.
                </p>
                <p>
                  We recruit, organize and dispatch hard-working crews for every kind of agricultural and
                  food-processing labour, so our clients can focus on the crop instead of the staffing. From a two-person
                  pruning job to a full harvest crew, one call handles it.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <a
                href={PHONE_TEL}
                data-testid="about-call-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#22C55E] px-7 py-4 text-sm font-semibold text-[#0F291E] transition-transform duration-300 hover:scale-105"
              >
                Call {PHONE_DISPLAY}
                <ArrowRight className="h-4 w-4" />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#0B1F17] px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>What We Stand For</Eyebrow>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div
                  data-testid={`value-card-${i}`}
                  className="h-full rounded-2xl border border-white/10 bg-[#163B2C]/40 p-7 transition-colors duration-500 hover:border-[#22C55E]/50"
                >
                  <v.icon className="h-6 w-6 text-[#22C55E]" />
                  <h3 className="mt-5 font-display text-xl font-semibold tracking-tight">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/60">{v.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>How Dispatch Works</Eyebrow>
          </Reveal>
          <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div className="group grid gap-4 py-10 transition-colors duration-500 hover:bg-[#163B2C]/40 sm:grid-cols-[100px_1fr_1.4fr] sm:items-baseline sm:gap-10 sm:px-6">
                  <span className="font-mono text-sm font-semibold text-[#22C55E]">{s.num}</span>
                  <h3 className="font-display text-2xl font-semibold tracking-tight transition-colors duration-300 group-hover:text-[#86EFAC]">
                    {s.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-[#F6F4EE]/65 sm:text-base">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex items-start gap-4 rounded-2xl border border-white/10 bg-[#163B2C]/40 p-6 sm:p-8">
              <Truck className="mt-1 h-6 w-6 shrink-0 text-[#22C55E]" />
              <p className="text-sm leading-relaxed text-[#F6F4EE]/70 sm:text-base">
                We operate across the Lower Mainland, Fraser Valley and Greater Vancouver. Crews are dispatched locally,
                which means faster response times and workers who know the region's farms and facilities.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10">
              <Link
                to="/services"
                data-testid="about-services-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#86EFAC]"
              >
                See everything our crews do
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
